import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import useSWR from 'swr';

import { SL_WEB_RATE_SURF } from 'common/treatments';
import { getRateTheSurf, postRateTheSurf, type GetRateTheSurfResponse } from 'common/api/kbyg';
import Eye from 'components/Icons/Eye';
import Shaka from 'components/Icons/Shaka';
import UserAvatar from 'components/Header/components/NavigationBar/components/UserAvatar';
import StarRating from 'components/StarRating';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import ErrorBoundary from 'components/ErrorBoundary';
import useConditions from 'hooks/useConditions';
import { getSpotDetails } from 'selectors/spot';
import { getUserDetails } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { RateSurfSample } from 'types/rateTheSurf';
import { useTreatments } from 'utils/treatments';

import RateSurfConditions from './components/RateSurfConditions';
import ImageSequence from './components/ImageSequence';

import styles from './RateSurf.module.scss';

type RatingState = 'idle' | 'submitting' | 'submitted';

interface SlideRatingData {
  rating: number | undefined;
  state: RatingState;
}

interface RateSurfSlideData extends RateSurfSample {
  id: string;
  name: string;
}

const RateSurfContents: FunctionComponent = () => {
  const treatments = useTreatments();
  const { themeClassName } = useConditions();
  const spot = useAppSelector(getSpotDetails);
  const userDetails = useAppSelector(getUserDetails);
  const theme = useTheme();

  // Get spot ID from Redux state
  const spotId = spot?._id;

  // Fetch rate surf data using SWR
  const {
    data: rateSurfData,
    error,
    isLoading,
    mutate,
  } = useSWR<GetRateTheSurfResponse | null, Error>(
    spotId ? `rate-surf-${spotId}` : null,
    () => (spotId ? getRateTheSurf({ spotId }) : null),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  // Transform API data to slide format
  const slides = useMemo((): RateSurfSlideData[] => {
    if (!rateSurfData?.samples) return [];

    return rateSurfData.samples.map((sample, index) => ({
      ...sample,
      id: `sample-${sample.timestamp}-${index}`,
      name: rateSurfData.spot.name,
    }));
  }, [rateSurfData]);

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [slideRatings, setSlideRatings] = useState<Record<number, SlideRatingData>>({});
  const [showConditions, setShowConditions] = useState<boolean>(true);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const rootClusteringId = rateSurfData?.clusteringId ?? null;

  const handleRatingChange = useCallback(
    async (slideIndex: number, rating: number) => {
      // Prevent rating changes during transition
      if (isTransitioning || !spotId) return;

      // Set loading state
      setSlideRatings((prev) => ({
        ...prev,
        [slideIndex]: { rating, state: 'submitting' },
      }));

      try {
        await postRateTheSurf({
          spotId,
          rating,
          timestamp: slides[slideIndex].timestamp,
          clusteringId: rootClusteringId,
        });

        // Set submitted state
        setSlideRatings((prev) => ({
          ...prev,
          [slideIndex]: { rating, state: 'submitted' },
        }));

        // Auto-advance to next slide after successful submission (1 second delay)
        const nextSlideIndex = slideIndex + 1;
        if (nextSlideIndex < slides.length) {
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentSlideIndex(nextSlideIndex);
            setIsTransitioning(false);
          }, 1000);
        }
      } catch (error) {
        console.error('Failed to submit rating:', error);
        // Reset to idle on error
        setSlideRatings((prev) => ({
          ...prev,
          [slideIndex]: { rating, state: 'idle' },
        }));
      }
    },
    [slides, isTransitioning, spotId, rootClusteringId],
  );

  const handleSkip = useCallback(async () => {
    if (isTransitioning || !spotId) return;
    setSlideRatings((prev) => ({
      ...prev,
      [currentSlideIndex]: { rating: undefined, state: 'submitting' },
    }));
    try {
      await postRateTheSurf({
        spotId,
        rating: null,
        timestamp: slides[currentSlideIndex].timestamp,
        clusteringId: rootClusteringId,
      });
      setSlideRatings((prev) => ({
        ...prev,
        [currentSlideIndex]: { rating: undefined, state: 'submitted' },
      }));
      const nextSlideIndex = currentSlideIndex + 1;
      if (nextSlideIndex < slides.length) {
        setCurrentSlideIndex(nextSlideIndex);
        setIsTransitioning(false);
      }
    } catch (error) {
      console.error('Failed to submit skipped rating:', error);
      setSlideRatings((prev) => ({
        ...prev,
        [currentSlideIndex]: { rating: undefined, state: 'idle' },
      }));
    }
  }, [slides, isTransitioning, currentSlideIndex, spotId, rootClusteringId]);

  const handleToggleConditions = useCallback(() => {
    setShowConditions((prev) => !prev);
  }, []);

  const handleStartOver = useCallback(async () => {
    setCurrentSlideIndex(0);
    setSlideRatings({});
    setShowConditions(true);
    setIsTransitioning(false);
    await mutate();
  }, [mutate]);

  const hasNextSlide = currentSlideIndex < slides.length - 1;
  const isLastSlide = currentSlideIndex === slides.length - 1;

  const slideRatingChangeHandlers = useMemo(
    () => slides.map((_, i) => (val: number) => handleRatingChange(i, val)),
    [slides, handleRatingChange],
  );

  const completedCount = useMemo(
    () => Object.values(slideRatings).filter((s) => s.state === 'submitted').length,
    [slideRatings],
  );

  const percentComplete = useMemo(
    () => (slides.length > 0 ? Math.round((completedCount / slides.length) * 100) : 0),
    [completedCount, slides],
  );

  const isCompleted = useMemo(
    () => slides.length > 0 && completedCount === slides.length,
    [completedCount, slides.length],
  );

  const userFirstName = useMemo(() => userDetails?.firstName ?? null, [userDetails]);

  const currentSlideState = useMemo(() => {
    const currentSlideRating = slideRatings[currentSlideIndex];
    const isCurrentSlideLoading = currentSlideRating?.state === 'submitting';
    const isCurrentSlideSubmitted = currentSlideRating?.state === 'submitted';

    return {
      currentSlideIndex,
      currentSlideRating,
      isCurrentSlideLoading,
      isCurrentSlideSubmitted,
    };
  }, [currentSlideIndex, slideRatings]);

  const dotItemsWithState = useMemo(() => {
    return slides.map((_, i) => {
      const slideRating = slideRatings[i];
      const isSubmitted = slideRating?.state === 'submitted';
      const isActive = i === currentSlideIndex;

      return {
        index: i,
        slideRating,
        isSubmitted,
        isActive,
      };
    });
  }, [slides, slideRatings, currentSlideIndex]);

  const currentSlideData = slides[currentSlideIndex];
  const currentSlideRating = slideRatings[currentSlideIndex];
  const currentRating = currentSlideRating?.rating;
  const isCurrentSlideLoading = currentSlideRating?.state === 'submitting';

  if (treatments?.[SL_WEB_RATE_SURF] !== 'on') {
    return (
      <Box className={classNames('sl-section-container', themeClassName, styles.container)}>
        <ErrorMessage
          message="Sorry, you do not have access."
          subMessage="This feature is not available for your account."
          dataTestId="rate-surf-no-access"
        />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className={classNames('sl-section-container', themeClassName, styles.container)}>
        <Box textAlign="center" data-testid="rate-surf-loading">
          <Typography variant="headline" component="h1">
            Rate the Surf
          </Typography>
          <CircularProgress color="secondary" size={60} className={styles.loading} />
        </Box>
      </Box>
    );
  }

  if (error || !rateSurfData) {
    return (
      <Box className={classNames('sl-section-container', themeClassName, styles.container)}>
        <Box textAlign="center" data-testid="rate-surf-error">
          <Typography variant="headline" component="h1" mb={2}>
            Rate the Surf
          </Typography>
          <Typography component="p" variant="body1" color="error">
            Unable to load surf data. Please try again later.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (slides.length === 0) {
    return (
      <Box className={classNames('sl-section-container', themeClassName, styles.container)}>
        <Box textAlign="center" data-testid="no-samples-message">
          <Typography variant="headline" component="h1" mb={2}>
            Sorry{userFirstName ? `, ${userFirstName}` : ''}
          </Typography>
          <Typography component="p" variant="body1" mb={2}>
            We have nothing for you to rate for {rateSurfData.spot.name} at this time. We are
            actively working on improving personalization.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={classNames('sl-section-container', themeClassName, styles.container)}
      data-testid="rate-surf-main"
    >
      <Box textAlign="center">
        <Typography variant="headline" component="h1" mb={2}>
          Rate the Surf
        </Typography>
        <Typography component="p" variant="footnote" mb={4}>
          Unlock personalized ratings for this spot.
        </Typography>
      </Box>
      <Typography component="p" variant="subtitle1" textAlign="center" mb={3}>
        {rateSurfData.spot.name}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          {dotItemsWithState.map(({ index, slideRating, isSubmitted, isActive }) => (
            <Box
              key={`dot-${index}`}
              className={classNames(styles.dot, {
                [styles.dotSubmitted]: isSubmitted,
                [styles.dotActive]: isActive,
              })}
              data-rating={isSubmitted ? (slideRating?.rating ?? 'skipped') : undefined}
            />
          ))}
        </Stack>
        <UserAvatar isPremiumPlusUser={false} progressPercent={percentComplete} />
      </Stack>
      {!isCompleted ? (
        <Box component="div">
          <div className="sl-section-full-bleed">
            <Box className={styles.slideContainer}>
              <Stack spacing={2} width="100%">
                <div style={{ position: 'relative' }}>
                  <ImageSequence
                    imageUrls={currentSlideData.camStillUrls}
                    className={styles.slideImage}
                  />
                  <Box className={styles.statusIndicators}>
                    {currentSlideState.isCurrentSlideLoading && (
                      <CircularProgress
                        size={24}
                        className={styles.spinnerIndicator}
                        data-rating={currentSlideState.currentSlideRating?.rating ?? 'unrated'}
                      />
                    )}
                    {currentSlideState.isCurrentSlideSubmitted && (
                      <Box
                        className={styles.successIndicator}
                        data-rating={currentSlideState.currentSlideRating?.rating ?? 'unrated'}
                      >
                        <Shaka
                          fill={
                            currentSlideState.currentSlideRating?.rating
                              ? theme.palette.common.white
                              : theme.palette.text.primary
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </div>
                <div>
                  <div className={styles.starRatingContainer}>
                    <StarRating
                      key={`star-rating-${currentSlideIndex}`}
                      value={currentRating}
                      onChange={slideRatingChangeHandlers[currentSlideIndex]}
                      isLoading={isCurrentSlideLoading || isTransitioning}
                      variant="forecast"
                    />
                    {(hasNextSlide || isLastSlide) && !isCompleted && (
                      <Button
                        key={`skip-button-${currentSlideIndex}`}
                        className={styles.skipButton}
                        onClick={handleSkip}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        size="small"
                        aria-label={isLastSlide ? 'Skip this sample' : 'Skip to next slide'}
                        disabled={isTransitioning}
                      >
                        Skip
                      </Button>
                    )}
                  </div>
                </div>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  justifyContent="center"
                  mt={2}
                  mb={showConditions ? 2 : 0}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={handleToggleConditions}
                    className={styles.customToggle}
                    aria-label="Toggle condition data"
                    aria-pressed={showConditions}
                  >
                    <Eye className={styles.toggleIcon} hidden={!showConditions} />
                    <Typography variant="callout1" className={styles.toggleText}>
                      {showConditions ? 'Hide forecast data' : 'Show forecast data'}
                    </Typography>
                  </Box>
                </Stack>

                {showConditions && rateSurfData && (
                  <div className="sl-section-container">
                    <RateSurfConditions
                      slideData={currentSlideData}
                      slideIndex={currentSlideIndex}
                      units={rateSurfData.units}
                    />
                  </div>
                )}
              </Stack>
            </Box>
          </div>
        </Box>
      ) : (
        <Box className={classNames('sl-section-container', themeClassName, styles.container)}>
          <Box textAlign="center">
            <Typography variant="headline" component="h1" mb={3}>
              {userFirstName ? `Great start, ${userFirstName}!` : 'Great start!'}
            </Typography>
            <Typography component="p" variant="body1" mb={4}>
              You&apos;ve improved your Personalized Ratings for &apos;{rateSurfData.spot.name}
              &apos;
            </Typography>
            <Box display="flex" justifyContent="center">
              <Button
                className={styles.startOverButton}
                disableFocusRipple
                disableRipple
                disableTouchRipple
                onClick={handleStartOver}
                size="small"
                variant="secondary"
              >
                Start over
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface RateSurfProps {
  error?: boolean;
}

export const RateSurf: React.FC<RateSurfProps> = (props) => {
  const { error } = props;
  return (
    <ErrorBoundary
      error={error}
      errorBoundaryId="rate-surf"
      render={() => (
        <Box
          className="sl-section-container"
          textAlign="center"
          data-testid="rate-surf-error-boundary"
        >
          <Typography variant="headline" component="h1" mb={2}>
            Rate the Surf
          </Typography>
          <ErrorMessage
            message="Something went wrong while loading the rating interface."
            subMessage="Please refresh the page and try again."
          />
        </Box>
      )}
    >
      <RateSurfContents />
    </ErrorBoundary>
  );
};

export default RateSurf;
